import * as React from 'react';
import { HoldingRowData } from './DataTable.d';
import classnames from 'classnames';
import { standardDateFormat } from '../../utils/dateFormatter';

import styles from './DataTable.module.scss';

type Props = {
  headers: string[];
  rowData: HoldingRowData[];
  selectedAccount?: AssetAccount;

  columnClasses?: { [k in string]: string }; // ['column name']: 'class-name'.
  tableFormatClass: string; // CSS class that has the data-grid info

  // these are used to display the mobile header
  labelHeader: string;
  valueHeader: string;
};

const DataTable: React.FC<Props> = (props) => {
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event === undefined) {
      return;
    }

    const { classList } = event.currentTarget;

    if (classList.contains(styles['mobile-collapsed'])) {
      classList.remove(styles['mobile-collapsed']);
      classList.add(styles['mobile-expanded']);
    } else {
      classList.remove(styles['mobile-expanded']);
      classList.add(styles['mobile-collapsed']);
    }
  };

  const makeRows = () => {
    const {
      headers,
      rowData,
      columnClasses = {},
      tableFormatClass,
      labelHeader,
      valueHeader
    } = props;
    if (rowData && rowData.length) {
      return rowData.map((row, ind) => {
        if (row) {
          const cells = headers.map((header, index) => {
            const isLabelHeader = header === labelHeader;
            const isInHeaderRow = row.isHeading;

            const isNormalCellHidden = isLabelHeader && !isInHeaderRow;
            const isHeaderCellHidden = !isLabelHeader && isInHeaderRow;

            const className = classnames(
              styles[`mobile-row-${index + 2}`],
              columnClasses[header]
            );

            const style = classnames(
              styles['data-cell'],
              styles['mobile-collapsible'],
              styles['ms-column-' + (2 * index + 1)],
              {
                [styles['no-mobile']]: isNormalCellHidden || isHeaderCellHidden,
                [styles['label-cell']]: isLabelHeader
              }
            );

            const isLabelInHeader = isInHeaderRow && isLabelHeader;
            const labelClassName = classnames({
              [styles['cell-value']]: true,
              [styles['left-aligned-cell']]: isLabelInHeader
            });

            return (
              <div key={header} className={`${className} ${style}`}>
                {!isInHeaderRow && (
                  <div className={styles['cell-name']}>{header}</div>
                )}
                <div className={labelClassName}>
                  {row[header] || row[header.toLowerCase()]}
                </div>
                {isLabelInHeader && (
                  <div
                    className={`${styles['cell-value']} ${styles['no-desktop']}`}>
                    {row[valueHeader] || row[valueHeader.toLowerCase()]}
                  </div>
                )}
              </div>
            );
          });

          if (!row.isHeading) {
            cells.unshift(
              <div
                role="button"
                key="mobile-open"
                className={`${styles['data-cell']} ${styles['no-desktop']} ${styles['mobile-toggle-cell']} ${styles['mobile-collapsed']} ${styles['mobile-row-1']} ${styles['mobile-title']}`}
                onClick={handleClick}>
                <div className={styles['cell-name-wrapper']}>
                  <div className={styles['cell-name']}>
                    {row[labelHeader] || row[labelHeader.toLowerCase()]}
                  </div>
                </div>
                <div className={styles['cell-value']}>
                  {row[valueHeader] || row[valueHeader.toLowerCase()]}
                </div>
              </div>
            );
          }

          const classNames = classnames(
            styles['table-row'],
            {
              [styles['header-row']]: row.isHeading,
              [styles['normal-row']]: !row.isHeading
            },
            styles['mobile-table-format']
          );

          const classes = classnames(tableFormatClass, row.rowClass);

          return (
            <div key={ind} className={`${classes} ${classNames}`}>
              {cells}
            </div>
          );
        }
        return null;
      });
    }
  };

  const {
    headers,
    tableFormatClass,
    labelHeader,
    valueHeader,
    selectedAccount
  } = props;
  const rowElements = makeRows();

  return (
    <div className={styles['data-table']}>
      <div
        className={classnames(
          `${tableFormatClass}`,
          styles['table-row'],
          styles['title-row'],
          styles['no-desktop'],
          styles['ms-column-1']
        )}>
        <div>{labelHeader}</div>
        <div>{valueHeader}</div>
      </div>
      <div
        className={classnames(
          `${tableFormatClass}`,
          styles['table-row'],
          styles['title-row'],
          styles['no-mobile']
        )}>
        {headers.map((h, index) => (
          <div className={styles['ms-column-' + (2 * index + 1)]} key={h}>
            {h}
            <div>
              {h === 'Since Inception'
                ? standardDateFormat(
                    selectedAccount?.monthlyReport?.inceptionDate ?? ''
                  )
                : ''}
            </div>
          </div>
        ))}
      </div>
      {rowElements}
    </div>
  );
};

export default DataTable;
